import { useMemo, useState } from "react";
import {
  Mail,
  MessageSquare,
  Phone,
  Search,
  Star,
  User,
  Calendar,
  MoreHorizontal,
  Flame,
} from "lucide-react";
import { PageHeader } from "@/components/admin/PageHeader";
import { ResourceState } from "@/components/admin/ResourceState";
import { useLeads } from "@/application/hooks/use-leads";
import { useVehicles } from "@/application/hooks/use-vehicles";
import type { Lead, LeadStatus } from "@/domain/models/lead";
import { formatPrice } from "@/domain/models/vehicle";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

const PIPELINE: LeadStatus[] = ["New", "Contacted", "Qualified", "Test Drive", "Won", "Lost"];
const STATUS_COLOR: Record<LeadStatus, string> = {
  New: "bg-primary/15 text-primary",
  Contacted: "bg-blue-500/15 text-blue-400",
  Qualified: "bg-violet-500/15 text-violet-400",
  "Test Drive": "bg-amber-500/15 text-amber-400",
  Won: "bg-emerald-500/15 text-emerald-400",
  Lost: "bg-rose-500/15 text-rose-400",
};

export function LeadsPage() {
  const { t, tf } = useI18n();
  const {
    status,
    data: leadsData,
    error,
    reload,
    updateStatus: persistStatus,
  } = useLeads();
  const { data: vehicles } = useVehicles();
  const leads = leadsData ?? [];
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "all">("all");
  const [selected, setSelected] = useState<Lead | null>(null);
  const [view, setView] = useState<"table" | "board">("table");

  const filtered = useMemo(
    () =>
      leads.filter((l) => {
        if (query && !`${l.name} ${l.email}`.toLowerCase().includes(query.toLowerCase())) return false;
        if (statusFilter !== "all" && l.status !== statusFilter) return false;
        return true;
      }),
    [leads, query, statusFilter],
  );

  const updateStatus = async (id: string, nextStatus: LeadStatus) => {
    try {
      await persistStatus(id, nextStatus);
      setSelected((prev) => (prev?.id === id ? { ...prev, status: nextStatus } : prev));
      toast.success(tf(t.admin.leads.movedTo, { status: t.admin.leads.statuses[nextStatus] }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.admin.leads.updateFail);
    }
  };

  const counts = useMemo(
    () =>
      PIPELINE.reduce(
        (acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }),
        {} as Record<LeadStatus, number>,
      ),
    [leads],
  );

  return (
    <div>
      <PageHeader
        title={t.admin.leads.title}
        description={t.admin.leads.description}
        actions={
          <div className="flex items-center gap-1 rounded-full border border-border/60 bg-surface/60 p-1">
            {(["table", "board"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition capitalize",
                  view === v
                    ? "bg-primary text-primary-foreground shadow-[var(--shadow-glow)]"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.admin.leads[v]}
              </button>
            ))}
          </div>
        }
      />

      <ResourceState
        status={status}
        error={error}
        onRetry={() => void reload()}
        emptyMessage={t.admin.leads.empty}
      >
      {/* Pipeline overview */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {PIPELINE.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
            className={cn(
              "rounded-xl border p-3 text-left transition hover:border-primary/40",
              statusFilter === s
                ? "border-primary/60 bg-primary/10"
                : "border-border/60 bg-surface-elevated/60",
            )}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t.admin.leads.statuses[s]}
            </div>
            <div className="mt-1 font-display text-2xl font-semibold tabular-nums">
              {counts[s]}
            </div>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t.admin.leads.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 border-border/60 bg-surface-elevated/60 pl-9"
          />
        </div>
      </div>

      {view === "table" ? (
        <div className="overflow-hidden rounded-2xl border border-border/60 bg-surface-elevated/60 backdrop-blur">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">{t.admin.leads.colLead}</th>
                  <th className="hidden px-4 py-3 text-left font-medium md:table-cell">{t.admin.leads.colVehicle}</th>
                  <th className="hidden px-4 py-3 text-left font-medium lg:table-cell">{t.admin.leads.colSource}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.admin.leads.colStatus}</th>
                  <th className="hidden px-4 py-3 text-left font-medium xl:table-cell">{t.admin.leads.colOwner}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.admin.leads.colValue}</th>
                  <th className="w-12 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.map((l) => {
                  const vehicle = vehicles?.find((v) => v.id === l.vehicleId);
                  return (
                    <tr
                      key={l.id}
                      onClick={() => setSelected(l)}
                      className="cursor-pointer transition hover:bg-muted/30"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary/40 to-primary/10 font-display text-xs font-semibold text-primary">
                            {l.name.split(" ").map((n) => n[0]).join("")}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 truncate font-semibold">
                              {l.name}
                              {l.priority === "High" && (
                                <Flame className="h-3 w-3 shrink-0 text-amber-400" />
                              )}
                            </div>
                            <div className="truncate text-[11px] text-muted-foreground">{l.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-4 py-3 md:table-cell">
                        {vehicle ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {vehicle.brand} {vehicle.model}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="hidden px-4 py-3 lg:table-cell">
                        <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/30 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          {l.source}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                            STATUS_COLOR[l.status],
                          )}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {t.admin.leads.statuses[l.status]}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 text-xs text-muted-foreground xl:table-cell">
                        {l.assignedTo}
                      </td>
                      <td className="px-4 py-3 font-semibold tabular-nums text-primary">
                        {formatPrice(l.value)}
                      </td>
                      <td className="px-4 py-3">
                        <button className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          {PIPELINE.map((s) => (
            <div key={s} className="flex flex-col rounded-2xl border border-border/60 bg-surface/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold", STATUS_COLOR[s])}>
                  {t.admin.leads.statuses[s]}
                </span>
                <span className="text-[11px] text-muted-foreground">{counts[s]}</span>
              </div>
              <div className="space-y-2">
                {leads
                  .filter((l) => l.status === s)
                  .slice(0, 5)
                  .map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setSelected(l)}
                      className="w-full rounded-lg border border-border/60 bg-surface-elevated/60 p-3 text-left transition hover:-translate-y-0.5 hover:border-primary/40"
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate text-xs font-semibold">{l.name}</span>
                        {l.priority === "High" && <Flame className="h-3 w-3 text-amber-400" />}
                      </div>
                      <div className="mt-1 truncate text-[10px] text-muted-foreground">
                        {vehicles?.find((v) => v.id === l.vehicleId)?.model ?? l.source}
                      </div>
                      <div className="mt-2 text-[11px] font-semibold text-primary">
                        {formatPrice(l.value)}
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg">
          {selected && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-primary/40 to-primary/10 font-display text-sm font-semibold text-primary">
                    {selected.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div className="flex flex-col text-left">
                    <span>{selected.name}</span>
                    <span className="font-mono text-[10px] font-normal text-muted-foreground">
                      {selected.id}
                    </span>
                  </div>
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {PIPELINE.map((s) => (
                    <button
                      key={s}
                      onClick={() => void updateStatus(selected.id, s)}
                      className={cn(
                        "rounded-md border px-2 py-1.5 text-xs font-medium transition",
                        selected.status === s
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border/60 text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {t.admin.leads.statuses[s]}
                    </button>
                  ))}
                </div>

                <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                  <div className="grid grid-cols-2 gap-y-3 text-xs">
                    <InfoRow icon={Mail} label={t.admin.leads.email} value={selected.email} />
                    <InfoRow icon={Phone} label={t.admin.leads.phone} value={selected.phone} />
                    <InfoRow icon={User} label={t.admin.leads.owner} value={selected.assignedTo} />
                    <InfoRow icon={Calendar} label={t.admin.leads.created} value={new Date(selected.createdAt).toLocaleDateString()} />
                    <InfoRow icon={MessageSquare} label={t.admin.leads.source} value={selected.source} />
                    <InfoRow icon={Star} label={t.admin.leads.priority} value={selected.priority} />
                  </div>
                </div>

                {selected.vehicleId && (() => {
                  const v = vehicles?.find((x) => x.id === selected.vehicleId);
                  if (!v) return null;
                  return (
                    <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-surface-elevated/60 p-3">
                      <img src={v.image} className="h-14 w-20 rounded-md object-cover" alt="" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">{v.brand} {v.model}</div>
                        <div className="text-[11px] text-muted-foreground">{v.year} · {v.body}</div>
                      </div>
                      <div className="text-sm font-semibold text-primary">{formatPrice(v.price)}</div>
                    </div>
                  );
                })()}

                <div>
                  <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">{t.admin.leads.message}</div>
                  <p className="rounded-xl border border-border/60 bg-muted/20 p-3 text-sm leading-relaxed">
                    {selected.message}
                  </p>
                </div>

                <div>
                  <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">{t.admin.leads.activity}</div>
                  <div className="space-y-2">
                    {[
                      { time: t.admin.leads.time2h, text: t.admin.leads.activityCreated },
                      { time: t.admin.leads.time1h, text: tf(t.admin.leads.activityAssigned, { owner: selected.assignedTo }) },
                      { time: t.admin.leads.time45m, text: t.admin.leads.activityOpened },
                    ].map((a) => (
                      <div key={a.text} className="flex items-start gap-3 rounded-md px-2 py-1.5 text-xs">
                        <div className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <div className="flex-1">
                          <div>{a.text}</div>
                          <div className="text-[10px] text-muted-foreground">{a.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button className="flex-1 rounded-md bg-primary py-2 text-xs font-semibold text-primary-foreground shadow-[var(--shadow-glow)]">
                    {t.admin.common.reply}
                  </button>
                  <button className="flex-1 rounded-md border border-border/60 py-2 text-xs font-medium">
                    {t.admin.leads.scheduleTestDrive}
                  </button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      </ResourceState>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: typeof User; label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </span>
      <span className="mt-0.5 truncate text-xs font-medium">{value}</span>
    </div>
  );
}
